import { useState, useEffect, useCallback, useMemo } from 'react';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import { listOrgMembers } from '../../services/api';
import {
  getAgencyClinics,
  type AgencyClinicResponse,
} from '../../services/clinicsApi';
import {
  listRogerDocAccountLinks,
  buildRogerDocLinksByEmail,
  type RogerDocClinicianAccount,
} from '../../services/accountLinkApi';
import { withAgencyClinicDisplayNames } from '../../utils/agencyClinicDisplayName';
import { DEFAULT_MEMBER_STATUS_FILTER, memberStatus } from './memberStatus';
import type { MemberStatus, OrgMemberResponse } from './types';

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useClinicianManager() {
  const { user, hasAnyPermission, showError } = useStore(
    useShallow(state => ({
      user: state.user,
      hasAnyPermission: state.hasAnyPermission,
      showError: state.showError,
    }))
  );

  // ── Data state ──
  const [members, setMembers] = useState<OrgMemberResponse[]>([]);
  const [clinics, setClinics] = useState<AgencyClinicResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<MemberStatus[]>([
    ...DEFAULT_MEMBER_STATUS_FILTER,
  ]);

  // ── RogerDoc account links (fetched separately so the member list renders
  //    even if RogerDoc is slow/unavailable; RogerDoc is the source of truth) ──
  const [rogerDocAccounts, setRogerDocAccounts] = useState<
    RogerDocClinicianAccount[]
  >([]);
  // Whether the fetch above actually succeeded. An empty list otherwise means
  // both "nobody has an account" and "we could not ask", and the health badge
  // must not report the second as the first.
  const [rogerDocAccountsLoaded, setRogerDocAccountsLoaded] = useState(false);

  // ── Invite dialog state ──
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  // ── Edit dialog state (RBAC dialog reads member + clinics from here) ──
  const [editingMember, setEditingMember] = useState<OrgMemberResponse | null>(
    null
  );

  // ── Data loading ──

  const loadData = useCallback(async () => {
    if (!user?.agency?.id) return;
    setIsLoading(true);
    try {
      const [membersRes, clinicsData] = await Promise.all([
        listOrgMembers({}),
        getAgencyClinics(user.agency.id),
      ]);
      setMembers(membersRes.data ?? []);
      // Resolved here so every clinic checkbox, badge and summary in the
      // manage and onboard dialogs has a label. A Kinnser default clinic has
      // no name of its own.
      setClinics(withAgencyClinicDisplayNames(clinicsData));
    } catch (err) {
      console.error('Failed to load clinician manager data:', err);
      showError('Failed to load organization members');
    } finally {
      setIsLoading(false);
    }
  }, [user?.agency?.id, showError]);

  const loadRogerDocLinks = useCallback(async () => {
    if (!user?.agency?.id) return;
    try {
      const accounts = await listRogerDocAccountLinks();
      setRogerDocAccounts(accounts);
      setRogerDocAccountsLoaded(true);
    } catch (err) {
      // Non-fatal: the member list stays usable without link info.
      setRogerDocAccountsLoaded(false);
      console.error('Failed to load RogerDoc account links:', err);
    }
  }, [user?.agency?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Separate, non-blocking fetch (per ticket: a separate call after FE loads).
  useEffect(() => {
    loadRogerDocLinks();
  }, [loadRogerDocLinks]);

  const rogerDocLinksByEmail = useMemo(
    () => buildRogerDocLinksByEmail(rogerDocAccounts, user?.agency?.id),
    [rogerDocAccounts, user?.agency?.id]
  );

  // EMR-375: every clinic is Roger-assignable (including Kinnser-participating).
  const roleAssignableClinics = clinics;

  // The member payload carries its own copy of each clinic name, so the table
  // badges need this to reach the same resolved names as the dialogs.
  const clinicNamesById = useMemo(
    () => new Map(clinics.map(clinic => [clinic.id, clinic.name])),
    [clinics]
  );

  // ── Filtered members (memoized) ──

  const { filteredMembers, showingOutsideFilter } = useMemo(() => {
    const tokens = searchQuery.toLowerCase().split(/\s+/).filter(Boolean);

    // Matched against one string so a query spanning two fields -- a full
    // name, most often -- can still match; every token has to land somewhere,
    // so extra words narrow rather than widen.
    const matchesSearch = (m: OrgMemberResponse) => {
      const haystack = [m.first_name, m.last_name, m.email, m.phone_number]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return tokens.every(token => haystack.includes(token));
    };

    const searched =
      tokens.length > 0 ? members.filter(matchesSearch) : members;
    const scoped = searched.filter(m => statusFilter.includes(memberStatus(m)));

    // The list opens without the outstanding invitations, so somebody
    // searching for a colleague who has not accepted theirs yet would not see
    // them, and nothing would say a filter is the reason. Whenever a search
    // matches anyone outside the checked statuses, show them and say so.
    if (tokens.length > 0 && searched.length > scoped.length) {
      return { filteredMembers: searched, showingOutsideFilter: true };
    }
    return { filteredMembers: scoped, showingOutsideFilter: false };
  }, [members, searchQuery, statusFilter]);

  // ── Edit dialog actions ──

  const openEditDialog = useCallback((member: OrgMemberResponse) => {
    setEditingMember(member);
  }, []);

  const closeEditDialog = useCallback(() => {
    setEditingMember(null);
  }, []);

  // ── Permission check ──

  const hasAccess = hasAnyPermission(['clinician-mgr:access', 'emr:manage']);

  return {
    // Permission
    hasAccess,

    // Data
    members,
    clinics,
    roleAssignableClinics,
    clinicNamesById,
    filteredMembers,
    showingOutsideFilter,
    isLoading,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    loadData,

    // RogerDoc account links
    rogerDocAccounts,
    rogerDocAccountsLoaded,
    rogerDocLinksByEmail,
    loadRogerDocLinks,

    // Invite dialog
    showInviteDialog,
    openInviteDialog: () => setShowInviteDialog(true),
    closeInviteDialog: () => setShowInviteDialog(false),

    // Edit dialog (MemberSettingsDialog uses bulk RBAC APIs internally)
    editingMember,
    openEditDialog,
    closeEditDialog,
  };
}
